/**
 * Shared icon vocabulary for the dashboard.
 *
 * Every glyph is an inline SVG copy of the Lucide shape with the same
 * stroke style we use in `CopyableCode.jsx`:
 *   - viewBox 0 0 24 24
 *   - fill: none
 *   - stroke: currentColor
 *   - strokeWidth: 2 (CheckIcon uses 2.5 for a chunkier tick)
 *   - strokeLinecap / strokeLinejoin: round
 *
 * Why inline (not the `lucide-react` package)?
 *   - No runtime dependency to pull in for ~30 glyphs.
 *   - No remote icon-CDN URLs — SVG XSS surface stays zero.
 *   - Matches the style already shipped by `CopyableCode.jsx` and the
 *     sidebar refresh in 050.
 *
 * Conventions:
 *   - Each icon accepts `size` (default 14, matches button text) and
 *     spreads remaining props onto the `<svg>` so callers can override
 *     `aria-hidden`, `className`, `style`, etc.
 *   - Render inside `<span aria-hidden="true">` next to a text label,
 *     OR set `aria-label` on the wrapping `<button>` for icon-only.
 *   - Never render user-supplied text inside the icon container —
 *     these components only emit static SVG `<path>` / `<polyline>`
 *     elements, so there is no XSS sink.
 *
 * Naming follows Lucide where possible so the next contributor finds
 * what they expect.  When in doubt: action verb (Save, Send, Edit),
 * not noun (FloppyDisk, Envelope, Pencil).
 */

function Icon({ size = 14, strokeWidth = 2, children, ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ── Save / commit ──────────────────────────────────────────────── */

// Lucide `save` — floppy outline.  Use for "Save", "Save changes".
export function SaveIcon(props) {
  return (
    <Icon {...props}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </Icon>
  );
}

// Lucide `check` — single tick.  Confirmation, "Apply", "Done".
// `strokeWidth` defaults to 2.5 to match CopyableCode's tick weight.
export function CheckIcon({ strokeWidth = 2.5, ...rest }) {
  return (
    <Icon strokeWidth={strokeWidth} {...rest}>
      <polyline points="20 6 9 17 4 12" />
    </Icon>
  );
}

/* ── CRUD actions ───────────────────────────────────────────────── */

// Lucide `pencil` — diagonal pencil over an edit underline.
export function EditIcon(props) {
  return (
    <Icon {...props}>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </Icon>
  );
}

// Lucide `trash-2`.  Pair with the `.btn--danger` red.
export function DeleteIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </Icon>
  );
}

// Lucide `plus` — "Add", "New", "Create".
export function AddIcon(props) {
  return (
    <Icon {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Icon>
  );
}

// Lucide `minus` — "Remove" (non-destructive) / detach.
export function RemoveIcon(props) {
  return (
    <Icon {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
    </Icon>
  );
}

/* ── Enable / disable / power ───────────────────────────────────── */

// Lucide `power` — toggle a feature off.  Pair with `.btn--danger`.
export function DisableIcon(props) {
  return (
    <Icon {...props}>
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </Icon>
  );
}

// Lucide `play` — re-enable / resume.
export function EnableIcon(props) {
  return (
    <Icon {...props}>
      <polygon points="5 3 19 12 5 21 5 3" />
    </Icon>
  );
}

/* ── Reveal / hide ──────────────────────────────────────────────── */

// Lucide `eye`.
export function EyeIcon(props) {
  return (
    <Icon {...props}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

// Lucide `eye-off`.
export function EyeOffIcon(props) {
  return (
    <Icon {...props}>
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 4.22-5.94" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-3.17 4.51" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </Icon>
  );
}

/* ── Copy / close ───────────────────────────────────────────────── */

// Lucide `copy`.  CopyableCode imports this.
export function CopyIcon(props) {
  return (
    <Icon {...props}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Icon>
  );
}

// Lucide `x` — close, dismiss, cancel.  NOT for destroy/delete.
export function XIcon(props) {
  return (
    <Icon {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Icon>
  );
}

/* ── Transfer ───────────────────────────────────────────────────── */

// Lucide `download` — file pointed at a tray.  CSV / JSON exports.
export function DownloadIcon(props) {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </Icon>
  );
}

// Lucide `upload`.
export function UploadIcon(props) {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </Icon>
  );
}

// Lucide `refresh-cw` — sync, reload, re-fetch.
export function RefreshIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </Icon>
  );
}

/* ── Chevrons ───────────────────────────────────────────────────── */

export function ChevronDownIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="6 9 12 15 18 9" />
    </Icon>
  );
}

export function ChevronUpIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="18 15 12 9 6 15" />
    </Icon>
  );
}

export function ChevronRightIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="9 18 15 12 9 6" />
    </Icon>
  );
}

export function ChevronLeftIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="15 18 9 12 15 6" />
    </Icon>
  );
}

/* ── Search / filter ────────────────────────────────────────────── */

// Lucide `search`.
export function SearchIcon(props) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Icon>
  );
}

// Lucide `filter` — funnel.
export function FilterIcon(props) {
  return (
    <Icon {...props}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </Icon>
  );
}

/* ── Links / arrows ─────────────────────────────────────────────── */

// Lucide `external-link`.
export function ExternalLinkIcon(props) {
  return (
    <Icon {...props}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </Icon>
  );
}

// Lucide `arrow-right` — "Apply", "Continue", "Next".
export function ArrowRightIcon(props) {
  return (
    <Icon {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </Icon>
  );
}

// Lucide `arrow-left` — "Back".
export function ArrowLeftIcon(props) {
  return (
    <Icon {...props}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </Icon>
  );
}

/* ── Alerts ─────────────────────────────────────────────────────── */

// Lucide `alert-triangle` — warning / pre-destructive.
export function AlertTriangleIcon(props) {
  return (
    <Icon {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Icon>
  );
}

// Lucide `info`.
export function InfoIcon(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </Icon>
  );
}

// Lucide `alert-circle` — error, blocking state.
export function AlertCircleIcon(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </Icon>
  );
}

/* ── Identity ───────────────────────────────────────────────────── */

// Lucide `user`.
export function UserIcon(props) {
  return (
    <Icon {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Icon>
  );
}

// Lucide `users`.
export function UsersIcon(props) {
  return (
    <Icon {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  );
}

// Lucide `mail`.
export function MailIcon(props) {
  return (
    <Icon {...props}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </Icon>
  );
}

// Lucide `key`.
export function KeyIcon(props) {
  return (
    <Icon {...props}>
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </Icon>
  );
}

/* ── Infra / web ────────────────────────────────────────────────── */

// Lucide `webhook` — three-arm hook.
export function WebhookIcon(props) {
  return (
    <Icon {...props}>
      <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
      <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
      <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
    </Icon>
  );
}

// Lucide `globe`.
export function GlobeIcon(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </Icon>
  );
}

// Lucide `code` — angle brackets, for snippet/install actions.
export function CodeIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </Icon>
  );
}

// Lucide `lock`.
export function LockIcon(props) {
  return (
    <Icon {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Icon>
  );
}

/* ── Pinpoint / messaging ───────────────────────────────────────── */

// Lucide `send` — paper plane.  Pinpoint replies, transactional sends.
export function SendIcon(props) {
  return (
    <Icon {...props}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </Icon>
  );
}

// Lucide `reply` — curved arrow.  Pinpoint reply button.
export function ReplyIcon(props) {
  return (
    <Icon {...props}>
      <polyline points="9 17 4 12 9 7" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </Icon>
  );
}

// Lucide `message-circle` — speech bubble.  Open Pinpoint thread.
export function MessageCircleIcon(props) {
  return (
    <Icon {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </Icon>
  );
}
